import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Product bundle - "Vaak samen gekocht" upsell op de productpagina.
 *
 * Regelt het gedrag binnen het bundel-blok (blocks/product-bundle.liquid):
 * - aan/uitvinken van producten en het activeren van de aantal-stepper
 * - plus/min-knoppen en het valideren van het aantal
 * - variantkeuze: bijwerken van checkbox-value, prijs en afbeelding
 * - popup met fotocarrousel per product
 * - subtotaalregel van de aangevinkte producten
 * - resetten van de selectie nadat iets aan de winkelwagen is toegevoegd
 *
 * Het daadwerkelijk toevoegen aan de winkelwagen gebeurt in product-form.js,
 * dat alle aangevinkte input[name="bundles"] meestuurt als items-array.
 */
class ProductBundleComponent extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('click', this.#onClick, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

    // Totaal bijwerken als de klant de variant of het aantal van het
    // hoofdproduct wijzigt.
    document.addEventListener(ThemeEvents.variantUpdate, this.#onMainVariantUpdate, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onMainQuantityUpdate, { signal });
    document.addEventListener('change', this.#onMainQuantityChange, { signal });

    this.#updateSubtotal();
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /** @returns {HTMLInputElement[]} */
  get #checkboxes() {
    return Array.from(this.querySelectorAll('input[name="bundles"]'));
  }

  /**
   * @param {Event} event
   */
  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    if (target instanceof HTMLInputElement && target.name === 'bundles') {
      this.#syncRow(target);
    } else if (target instanceof HTMLSelectElement && target.hasAttribute('data-bundle-variant')) {
      this.#onVariantChange(target);
    } else if (target instanceof HTMLInputElement && target.hasAttribute('data-bundle-qty')) {
      this.#sanitizeQuantity(target);
    }

    this.#updateSubtotal();
  };

  /**
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    // Klik op de backdrop van een open popup sluit de popup.
    if (target instanceof HTMLDialogElement && target.classList.contains('product-bundle__popup')) {
      target.close();
      return;
    }

    const button = target.closest('button');
    if (!button || !this.contains(button)) return;

    if (button.hasAttribute('data-bundle-plus') || button.hasAttribute('data-bundle-minus')) {
      const step = button.hasAttribute('data-bundle-plus') ? 1 : -1;
      const quantityInput = button
        .closest('.product-bundle__qty')
        ?.querySelector('[data-bundle-qty]');

      if (quantityInput instanceof HTMLInputElement) {
        const current = parseInt(quantityInput.value, 10);
        const next = (Number.isNaN(current) ? 1 : current) + step;
        quantityInput.value = String(this.#clampQuantity(quantityInput, next));
        this.#syncQuantityButtons(quantityInput);
        this.#updateSubtotal();
      }
      return;
    }

    const popupTarget = button.getAttribute('data-popup-target');
    if (popupTarget) {
      const dialog = document.getElementById(popupTarget);
      if (dialog instanceof HTMLDialogElement) dialog.showModal();
      return;
    }

    if (button.hasAttribute('data-popup-close')) {
      const dialog = button.closest('dialog');
      if (dialog instanceof HTMLDialogElement) dialog.close();
      return;
    }

    if (button.hasAttribute('data-carousel-prev') || button.hasAttribute('data-carousel-next')) {
      const direction = button.hasAttribute('data-carousel-next') ? 1 : -1;
      const track = button
        .closest('.product-bundle__carousel')
        ?.querySelector('[data-carousel-track]');

      if (track instanceof HTMLElement) {
        track.scrollBy({ left: direction * track.clientWidth, behavior: 'smooth' });
      }
    }
  };

  /**
   * Reset de selectie nadat het productformulier iets aan de winkelwagen
   * heeft toegevoegd.
   * @param {Event} event
   */
  #onCartUpdate = (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail;
    if (detail?.data?.didError) return;
    if (detail?.data?.source !== 'product-form-component') return;

    for (const checkbox of this.#checkboxes) {
      checkbox.checked = false;
      this.#syncRow(checkbox);
    }
    this.#updateSubtotal();
  };

  /**
   * Werkt de prijs van het hoofdproduct bij na een variantwissel.
   * @param {Event} event
   */
  #onMainVariantUpdate = (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail;
    if (detail?.data?.productId?.toString() !== this.dataset.productId) return;

    const price = detail.resource?.price;
    if (typeof price === 'number') this.dataset.mainPrice = String(price);

    // Kort uitstellen: het productformulier vervangt (morpht) de koopknop bij
    // een variantwissel, daarna pas de prijs opnieuw plaatsen.
    setTimeout(() => this.#updateSubtotal(), 50);
  };

  #onMainQuantityUpdate = () => {
    this.#updateSubtotal();
  };

  /**
   * @param {Event} event
   */
  #onMainQuantityChange = (event) => {
    const target = event.target;
    if (
      target instanceof HTMLInputElement &&
      target.name === 'quantity' &&
      target.form?.getAttribute('id') === this.dataset.productFormId
    ) {
      this.#updateSubtotal();
    }
  };

  /**
   * Het gekozen aantal van het hoofdproduct.
   * @returns {number}
   */
  #getMainQuantity() {
    const form = document.getElementById(this.dataset.productFormId ?? '');
    if (!(form instanceof HTMLFormElement)) return 1;
    const value = parseInt(String(new FormData(form).get('quantity') ?? ''), 10);
    return Number.isNaN(value) || value < 1 ? 1 : value;
  }

  /**
   * Zet de aantal-stepper aan of uit op basis van de checkbox.
   * @param {HTMLInputElement} checkbox
   */
  #syncRow(checkbox) {
    const row = checkbox.closest('.product-bundle__item');
    if (!row) return;

    const enabled = checkbox.checked && !checkbox.disabled;
    const quantityInput = row.querySelector('[data-bundle-qty]');
    const minusButton = row.querySelector('[data-bundle-minus]');
    const plusButton = row.querySelector('[data-bundle-plus]');

    if (quantityInput instanceof HTMLInputElement) {
      quantityInput.disabled = !enabled;
      if (!enabled) quantityInput.value = '1';
    }
    if (minusButton instanceof HTMLButtonElement) minusButton.disabled = !enabled;
    if (plusButton instanceof HTMLButtonElement) plusButton.disabled = !enabled;

    if (enabled && quantityInput instanceof HTMLInputElement) {
      this.#syncQuantityButtons(quantityInput);
    }
  }

  /**
   * Werkt checkbox, prijs, uitverkocht-label en afbeelding bij na een
   * variantwissel.
   * @param {HTMLSelectElement} select
   */
  #onVariantChange(select) {
    const option = select.selectedOptions?.[0];
    const row = select.closest('.product-bundle__item');
    if (!option || !row) return;

    const checkbox = row.querySelector('input[name="bundles"]');
    const available = !option.disabled;

    if (checkbox instanceof HTMLInputElement) {
      checkbox.value = option.value;
      checkbox.setAttribute('data-price', option.getAttribute('data-price') ?? '0');
      checkbox.disabled = !available;
      if (!available) checkbox.checked = false;
      this.#syncRow(checkbox);
    }

    const soldOutLabel = row.querySelector('[data-bundle-soldout]');
    if (soldOutLabel instanceof HTMLElement) soldOutLabel.hidden = available;

    const priceElement = row.querySelector('[data-bundle-price]');
    const price = Number(option.getAttribute('data-price'));
    if (priceElement instanceof HTMLElement && !Number.isNaN(price)) {
      priceElement.textContent = this.#formatMoney(price);
    }

    const image = row.querySelector('.product-bundle__media img');
    const imageSrc = option.getAttribute('data-img-src');
    if (image instanceof HTMLImageElement && imageSrc) {
      image.src = imageSrc;
      image.removeAttribute('srcset');
    }
  }

  /**
   * @param {HTMLInputElement} quantityInput
   */
  #sanitizeQuantity(quantityInput) {
    const value = parseInt(quantityInput.value, 10);
    quantityInput.value = String(this.#clampQuantity(quantityInput, Number.isNaN(value) ? 1 : value));
    this.#syncQuantityButtons(quantityInput);
  }

  /**
   * @param {HTMLInputElement} quantityInput
   * @param {number} value
   * @returns {number}
   */
  #clampQuantity(quantityInput, value) {
    const min = parseInt(quantityInput.min, 10) || 1;
    const max = parseInt(quantityInput.max, 10) || 99;
    return Math.min(max, Math.max(min, value));
  }

  /**
   * @param {HTMLInputElement} quantityInput
   */
  #syncQuantityButtons(quantityInput) {
    const wrapper = quantityInput.closest('.product-bundle__qty');
    if (!wrapper) return;

    const value = parseInt(quantityInput.value, 10) || 1;
    const min = parseInt(quantityInput.min, 10) || 1;
    const max = parseInt(quantityInput.max, 10) || 99;

    const minusButton = wrapper.querySelector('[data-bundle-minus]');
    const plusButton = wrapper.querySelector('[data-bundle-plus]');

    if (minusButton instanceof HTMLButtonElement) {
      minusButton.disabled = quantityInput.disabled || value <= min;
    }
    if (plusButton instanceof HTMLButtonElement) {
      plusButton.disabled = quantityInput.disabled || value >= max;
    }
  }

  #updateSubtotal() {
    const subtotalRow = this.querySelector('[data-bundle-subtotal-row]');
    const subtotalValue = this.querySelector('[data-bundle-subtotal]');
    if (!(subtotalRow instanceof HTMLElement) || !(subtotalValue instanceof HTMLElement)) return;

    // Hoofdproduct telt mee in het totaal.
    const mainPrice = Number(this.dataset.mainPrice) || 0;
    let subtotal = mainPrice * this.#getMainQuantity();

    for (const checkbox of this.#checkboxes) {
      if (!checkbox.checked || checkbox.disabled) continue;

      const price = Number(checkbox.getAttribute('data-price'));
      if (Number.isNaN(price)) continue;

      const quantityInput = checkbox
        .closest('.product-bundle__item')
        ?.querySelector('[data-bundle-qty]');
      const quantity =
        quantityInput instanceof HTMLInputElement ? parseInt(quantityInput.value, 10) || 1 : 1;

      subtotal += price * quantity;
    }

    subtotalRow.hidden = subtotal <= 0;
    subtotalValue.textContent = this.#formatMoney(subtotal);
    this.#updateBuyButtonPrice(subtotal);
  }

  /**
   * Toont de totaalprijs op de koopknop en houdt die actueel.
   * @param {number} subtotal - Totaal in centen (hoofdproduct + bundels)
   */
  #updateBuyButtonPrice(subtotal) {
    const form = document.getElementById(this.dataset.productFormId ?? '');
    const button = form?.querySelector('button[name="add"]');
    if (!button || subtotal <= 0) return;

    const content = button.querySelector('.add-to-cart-text__content') ?? button;
    let priceElement = button.querySelector('[data-bundle-buy-price]');

    if (!priceElement) {
      priceElement = document.createElement('span');
      priceElement.setAttribute('data-bundle-buy-price', '');
      priceElement.className = 'product-bundle__buy-price';
      content.appendChild(priceElement);
    }

    priceElement.textContent = ` · ${this.#formatMoney(subtotal)}`;
  }

  /**
   * @param {number} value - Bedrag in centen
   * @returns {string}
   */
  #formatMoney(value) {
    const format = this.dataset.moneyFormat || '€{{amount_with_comma_separator}}';
    const currency = this.dataset.currency || 'EUR';
    return formatMoney(value, format, currency);
  }
}

if (!customElements.get('product-bundle-component')) {
  customElements.define('product-bundle-component', ProductBundleComponent);
}
